import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useT } from "@/contexts/I18nContext";

/**
 * A dialog.
 *
 * The native `<dialog>` element, so the browser handles the focus trap, the
 * backdrop, Escape, and inertness of the page behind it. Reimplementing those
 * by hand is how a modal ends up unusable with a keyboard.
 */
export function Modal({
  open,
  onClose,
  titleKey,
  titleParams,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  titleKey: string;
  titleParams?: Record<string, unknown>;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Escape fires `cancel`, and the default would close the element without
    // telling React, leaving `open` true and the dialog unopenable afterwards.
    const onCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="modal-title"
      className="w-full max-w-lg rounded-lg border border-paper-deep bg-white p-0
                 text-ink backdrop:bg-ink/40"
    >
      <div className="flex items-start justify-between border-b border-paper-warm px-6 py-4">
        <h2 id="modal-title" className="font-display text-lg">
          {t(titleKey, titleParams)}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="rounded p-1 text-ink-muted hover:bg-paper-warm hover:text-ink"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
      {footer ? (
        <div className="flex justify-end gap-3 border-t border-paper-warm px-6 py-4">{footer}</div>
      ) : null}
    </dialog>
  );
}
